import React, { useState } from 'react';
import { AreaSettingsContext } from '../../hooks/useAreaModal';
import { FLOOR_AREA_DRAFT_NOTE } from '../../../../../constants/infoTexts';

const MAX_AREA_VALUE = 1000000; // 1 million m² max

interface AreaSettingsModalProps {
    context: AreaSettingsContext;
    onSave: (newArea: number) => Promise<void>;
    onClose: () => void;
}

/**
 * Modal for editing component area (mängd).
 * Saves the area using ComplexShape geometry type.
 *
 * Note: State is initialized from props. The parent should use a key prop
 * to force remount when context changes (e.g., key={context.componentKey}).
 */
export const AreaSettingsModal: React.FC<AreaSettingsModalProps> = ({
    context,
    onSave,
    onClose
}) => {
    const { componentName, currentArea, inVerifiedRoom } = context;

    const [areaValue, setAreaValue] = useState<string>(
        currentArea !== null ? currentArea.toString() : ''
    );
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        const numValue = parseFloat(areaValue);
        if (isNaN(numValue) || numValue < 0) {
            setError('Ange ett giltigt positivt tal');
            return;
        }
        if (numValue > MAX_AREA_VALUE) {
            setError(`Maxvärde är ${MAX_AREA_VALUE.toLocaleString('sv-SE')} m²`);
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            await onSave(numValue);
        } catch (err) {
            console.error('Error saving area:', err);
            setError('Kunde inte spara. Försök igen.');
            setIsSaving(false);
        }
    };

    const handleAreaChange = (value: string) => {
        setAreaValue(value);
        setError(null);
    };

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
                    <div className="modal-content">
                        {/* Header */}
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Mängd - {componentName}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Stäng"
                                disabled={isSaving}
                            />
                        </div>

                        {/* Body */}
                        <div className="modal-body">
                            {inVerifiedRoom && (
                                <div className="alert alert-info py-2" style={{ fontSize: '0.85rem' }} role="note">
                                    {FLOOR_AREA_DRAFT_NOTE}
                                </div>
                            )}
                            <div className="mb-3">
                                <label className="form-label" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                    Area (m²)
                                </label>
                                <input
                                    type="number"
                                    className={`form-control ${error ? 'is-invalid' : ''}`}
                                    value={areaValue}
                                    onChange={(e) => handleAreaChange(e.target.value)}
                                    min="0"
                                    max={MAX_AREA_VALUE}
                                    step="0.1"
                                    placeholder="Ange area i m²"
                                    disabled={isSaving}
                                    autoFocus
                                />
                                {error && (
                                    <div className="invalid-feedback">{error}</div>
                                )}
                                <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                                    Ange komponentens totala area i kvadratmeter.
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Avbryt
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={isSaving || !areaValue}
                            >
                                {isSaving ? 'Sparar...' : 'Spara'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
